import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  Box,
  Button,
  IconButton,
  Typography,
  TextField,
  Grid,
} from "@mui/material";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { fetchOwnerCompanies } from "../TruckPopup/TruckPopup";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";

interface InvoicePopupProps {
  rent: {
    id: string;
    truckId: string | null;
    trailerId: string | null;
    unitNumber: string;
    billToId: string;
    billToCompanyName: string;
    billingFrequency: string;
    baseRentalRate: number;
    make?: string;
    model?: string;
    vin?: string;
    licensePlate?: string;
  };
}

const InvoicePopup = ({ rent }: InvoicePopupProps) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [initialValues, setInitialValues] = useState({
    issuerCompanyId: "",
    billToCompany: "",
    invoiceDate: new Date().toISOString().split("T")[0],
    dueDate: "",
    taxPercent: 0,
  });
  const { token } = useAuth();

  const { data: ownerCompanies } = useQuery({
    queryKey: ["ownerCompanies"],
    queryFn: () => fetchOwnerCompanies(token),
    enabled: !!open,
  });

  useEffect(() => {
    if (open) {
      setInitialValues({
        issuerCompanyId: "",
        billToCompany: rent.billToCompanyName,
        invoiceDate: new Date().toISOString().split("T")[0],
        dueDate: "",
        taxPercent: 0,
      });

      if (items.length === 0) {
        const unitDescription = `${rent.billingFrequency}, ${
          rent.truckId ? "Truck" : "Trailer"
        } rental, Unit ${rent.unitNumber}${
          rent.make ? `, Make: ${rent.make}` : ""
        }${rent.model ? `, Model: ${rent.model}` : ""}${
          rent.vin ? `, VIN: ${rent.vin}` : ""
        }${rent.licensePlate ? `, License Plate: ${rent.licensePlate}` : ""}`;
        setItems([
          {
            date: new Date().toISOString().split("T")[0],
            activity: `Unit ${rent.unitNumber}`,
            description: unitDescription,
            quantity: 1,
            rate: rent.baseRentalRate,
          },
        ]);
      }
    }
  }, [open, rent]);

  const invoiceMutation = useMutation({
    mutationFn: (data: any) => {
      const payload = {
        issuerCompanyId: getId(data.issuerCompanyId),
        billToCompanyId: getId(rent.billToId),
        invoiceDate: data.invoiceDate,
        dueDate: data.dueDate,
        taxPercent: data.taxPercent,
        items: items,
        truckId: rent.truckId,
        trailerId: rent.trailerId,
      };
      return axios.post(`${apiVariable}/invoice`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
    },
    onSuccess: (response) => {
      setStatus("success");
      setOpen(false);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to generate invoice");
    },
  });

  const handleAddItem = () => {
    setItems((prevItems) => [
      ...prevItems,
      { date: "", activity: "", description: "", quantity: 0, rate: 0 },
    ]);
  };

  const handleRemoveItem = (index: number) => {
    setItems((prevItems) => {
      const newItems = [...prevItems];
      newItems.splice(index, 1);
      return newItems;
    });
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    setItems((prevItems) => {
      const newItems = [...prevItems];
      newItems[index] = { ...newItems[index], [field]: value };
      return newItems;
    });
  };

  const submitHandler = (data: any) => {
    if (items.length === 0) {
      setStatus("error");
      setErrorMessage("Please add at least one item");
      return;
    }
    invoiceMutation.mutate(data);
  };

  const renderItemFields = (item: any, index: number) => {
    return (
      <Grid container spacing={2} key={index}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Date"
            type="date"
            value={item.date}
            onChange={(e) => handleItemChange(index, "date", e.target.value)}
            InputLabelProps={{ shrink: true }}
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Activity"
            value={item.activity}
            onChange={(e) =>
              handleItemChange(index, "activity", e.target.value)
            }
            margin="normal"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            value={item.description}
            onChange={(e) =>
              handleItemChange(index, "description", e.target.value)
            }
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Quantity"
            type="number"
            value={item.quantity}
            onChange={(e) =>
              handleItemChange(index, "quantity", Number(e.target.value))
            }
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Rate"
            type="number"
            value={item.rate}
            onChange={(e) =>
              handleItemChange(index, "rate", Number(e.target.value))
            }
            margin="normal"
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Generate Invoice
      </Button>
      <GenericFormPopup
        formFields={[
          {
            name: "issuerCompanyId",
            label: "Issuer Company",
            type: "combo",
            dataKey: "ownerCompanies",
            validation: { required: "Issuer company is required" },
          },
          {
            name: "billToCompany",
            label: "Bill To Company",
            type: "text",
            disabled: true,
          },
          {
            name: "invoiceDate",
            label: "Invoice Date",
            type: "date",
            validation: { required: "Invoice date is required" },
          },
          {
            name: "dueDate",
            label: "Due Date",
            type: "date",
            validation: { required: "Due date is required" },
          },
          {
            name: "taxPercent",
            label: "Tax Percent",
            type: "number",
            validation: { required: "Tax percent is required" },
          },
        ]}
        initialValues={initialValues}
        title="Generate Invoice"
        onSubmit={submitHandler}
        open={open}
        onClose={() => {
          setOpen(false);
          setItems([]);
        }}
        autocompleteData={{ ownerCompanies }}
        loading={invoiceMutation.isPending}
        additionalComponents={
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">Invoice Items</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                variant="contained"
                color="primary"
              >
                Add Item
              </Button>
            </Box>
            {items.map((item, index) => (
              <Box
                key={index}
                sx={{ mb: 2, p: 2, border: "1px solid #ccc", borderRadius: 1 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1">Item {index + 1}</Typography>
                  {index > 0 && (
                    <IconButton
                      onClick={() => handleRemoveItem(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
                {renderItemFields(item, index)}
              </Box>
            ))}
          </Box>
        }
      />
      <StatusSnackbar
        status={status}
        successMessage="Invoice generated successfully"
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
      />
    </>
  );
};

export default InvoicePopup;
